<template>
    <PrimeDialog
        v-model:visible="internalVisible"
        :modal="true"
        :closable="true"
        :draggable="false"
        :style="{ width: '450px' }"
        :header="dialogHeader"
    >
        <div class="confirm-content">
            <div v-if="level === 1" class="level-1">
                <p>В таблице {{ rowCount }} {{ pluralize(rowCount, 'строка', 'строки', 'строк') }}.</p>
                <p>{{ replacementText }}</p>
            </div>

            <div v-else-if="level === 2" class="level-2">
                <div class="warning-icon">
                    <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #f59e0b;"></i>
                </div>
                <p class="row-count-text">В таблице <strong>{{ rowCount }}</strong> {{ pluralize(rowCount, 'строка', 'строки', 'строк') }} данных.</p>
                <p class="danger-text">
                    {{ dangerText }}
                </p>
            </div>

            <div v-else-if="level === 3" class="level-3">
                <div class="danger-icon">
                    <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #dc2626;"></i>
                </div>
                <p class="row-count-text big">В таблице <strong>{{ rowCount }}</strong> {{ pluralize(rowCount, 'строка', 'строки', 'строк') }} данных!</p>
                <p class="danger-text">
                    {{ largeDangerText }}
                </p>
                <div class="confirm-input-section">
                    <label>Для подтверждения введите количество строк, которые будут удалены:</label>
                    <InputText
                        v-model="confirmInput"
                        type="number"
                        :placeholder="String(rowCount)"
                        class="confirm-input"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <PrimeButton
                label="Отмена"
                class="p-button-secondary"
                @click="onCancel"
            />
            <PrimeButton
                v-if="level < 3"
                label="Заменить"
                :class="level === 2 ? 'p-button-danger' : ''"
                @click="onConfirm"
            />
            <PrimeButton
                v-else
                label="Удалить и заменить"
                class="p-button-danger"
                :disabled="confirmInput !== String(rowCount)"
                @click="onConfirm"
            />
        </template>
    </PrimeDialog>
</template>

<script>
export default {
    name: "DataReplaceConfirm",
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        rowCount: {
            type: Number,
            required: true
        },
        action: {
            type: String,
            default: 'upload' // 'upload' | 'change_keys' | 'change_mode'
        }
    },
    emits: ['confirm', 'cancel'],
    data() {
        return {
            confirmInput: ''
        };
    },
    computed: {
        level() {
            if (this.rowCount <= 0) return 0;
            if (this.rowCount < 10) return 1;
            if (this.rowCount <= 100) return 2;
            return 3;
        },
        dialogHeader() {
            if (this.action === 'change_keys') return 'Заменить структуру полей?';
            if (this.action === 'change_mode') return 'Изменить источник данных?';
            if (this.level === 1) return 'Заменить данные?';
            if (this.level === 2) return 'Внимание';
            return 'Внимание: большой объём данных!';
        },
        replacementText() {
            if (this.action === 'change_keys') return 'Новая структура полей заменит все текущие данные.';
            if (this.action === 'change_mode') return 'Смена источника данных очистит текущую таблицу.';
            return 'Загрузка нового файла заменит все текущие данные.';
        },
        dangerText() {
            if (this.action === 'change_keys') {
                return 'Новая структура полей безвозвратно удалит все текущие данные. Это действие нельзя отменить.';
            }
            if (this.action === 'change_mode') {
                return 'Смена источника данных безвозвратно удалит все текущие данные. Это действие нельзя отменить.';
            }
            return 'Загрузка нового файла безвозвратно удалит все текущие данные и заменит их содержимым файла. Это действие нельзя отменить.';
        },
        largeDangerText() {
            if (this.action === 'change_keys') {
                return `Новая структура полей безвозвратно удалит все ${this.rowCount} строк. Если данные были получены через API или загружены ранее, восстановить их будет невозможно.`;
            }
            if (this.action === 'change_mode') {
                return `Смена источника данных безвозвратно удалит все ${this.rowCount} строк. Если данные были получены через API или загружены ранее, восстановить их будет невозможно.`;
            }
            return `Загрузка нового файла безвозвратно удалит все ${this.rowCount} строк и заменит их содержимым файла. Если данные были получены через API или загружены ранее, восстановить их будет невозможно.`;
        },
        internalVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.onCancel();
                }
            }
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.confirmInput = '';
            }
        }
    },
    methods: {
        pluralize(n, one, few, many) {
            if (n % 10 === 1 && n % 100 !== 11) return one;
            if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return few;
            return many;
        },
        onConfirm() {
            if (this.level === 3 && this.confirmInput !== String(this.rowCount)) return;
            this.$emit('confirm', this.rowCount);
        },
        onCancel() {
            this.$emit('cancel');
        }
    }
}
</script>

<style scoped>
.confirm-content {
    padding: 16px 0;
}

.level-1 p,
.level-2 p,
.level-3 p {
    margin: 0 0 12px 0;
    line-height: 1.5;
}

.warning-icon,
.danger-icon {
    margin-bottom: 16px;
}

.row-count-text {
    font-size: 1.1rem;
}

.row-count-text.big {
    font-size: 1.25rem;
}

.danger-text {
    color: #dc2626;
}

.confirm-input-section {
    margin-top: 20px;
}

.confirm-input-section label {
    display: block;
    margin-bottom: 8px;
    color: #6c757d;
}

.confirm-input {
    width: 100%;
}
</style>
